/**
 * Determine whether a country value is a commonly used United States label.
 *
 * Matching is case-insensitive and supports the full country name as well as
 * the common `US` and `USA` abbreviations.
 */
export const isUnitedStatesCountry = (country?: string | null): boolean =>
  country
    ? ['united states', 'us', 'usa'].includes(country.trim().toLowerCase())
    : false;

/**
 * Format partial postal-address fields as a single readable line.
 *
 * Empty fields are omitted. United States country labels are also omitted
 * because domestic addresses do not need the country repeated; other country
 * names are retained. Returns `undefined` when no address fields are present.
 *
 * @param address1 The primary street-address line.
 * @param address2 The secondary street-address line.
 * @param city The city or locality.
 * @param state The state, province, or region.
 * @param postalCode The postal or ZIP code.
 * @param country The country name or abbreviation.
 */
export const formatPostalAddress = (
  address1?: string | null,
  address2?: string | null,
  city?: string | null,
  state?: string | null,
  postalCode?: string | null,
  country?: string | null,
): string | undefined => {
  const street = [address1, address2].filter(Boolean).join(', ');
  const cityState = [city, state].filter(Boolean).join(', ');
  const locality = [cityState, postalCode].filter(Boolean).join(' ');
  const formattedCountry =
    country && !isUnitedStatesCountry(country) ? country : null;
  const formattedAddress = [street, locality, formattedCountry]
    .filter(Boolean)
    .join(', ');

  return formattedAddress || undefined;
};
